import { FieldOptionBase, FieldTypeDisplay } from 'module/api/field/types';
import { Field } from 'module/api/field/types';
import { byNumber } from 'module/helpers/comparators/by-number';
import { fullName } from 'module/helpers/full-name';
import { FieldType } from 'module/types/filter-field/field/field';
import {
  AssigneeValue,
  FilterableField,
  MultiSelectField,
  SingleSelectField,
} from 'module/types/filter-field/field/filterable-field';
import { Comparator } from 'module/ui/components/atoms/grid/plugins/with-columns';
import { memoize } from 'lodash';

type Nullable<T> = T | null;
type OptionsField = SingleSelectField | MultiSelectField;

type NewField = FilterableField &
  Pick<
    Field,
    | 'fieldDataType'
    | 'fieldType'
    | 'optionsAvailable'
    | 'isEnabled'
    | 'isEditable'
  >;

export const getBaseComparator = <T = any>(
  field: Pick<NewField, 'metaData' | 'fieldName' | 'fieldTypeDisplay' | 'type'>,
  //projectId?: number,
): Comparator<T> | undefined => {
  // const activeUsersAndTeams =
  // activeUsersAndTeamsFromIndexedB.getActiveUsersAndTeams;

  const { type, fieldTypeDisplay } = field;
  const options =
    (field as OptionsField).metaData?.options ?? ([] as FieldOptionBase[]);
  //const metadata = field.metaData as
  //  | (UserFieldMetadata & {
  //      teams?: [];
  //      projectPlaceholders?: { [id: number]: [] };
  //    })
  //  | undefined;
  //const { teams: teamList } = { teams: [] };
  //const _teams = teamList ?? [];

  //const projectPlaceholders =
  //projectId && metadata?.projectPlaceholders
  //  ? (metadata?.projectPlaceholders?.[projectId] ?? [])
  //  : [];
  //const teams = uniqBy([..._teams, ...projectPlaceholders], 'teamId');

  const getLabel = memoize(
    (value: Nullable<number>) =>
      value == null ? null : options.find((o) => o.value === value)?.label,
    (value) => value?.toString() ?? 'null',
  );

  const getPosition = memoize(
    (value: Nullable<number>) =>
      value == null ? null : options.find((o) => o.value === value)?.position,
    (value) => value?.toString() ?? 'null',
  );

  const getMultiSelectLabel = memoize(
    (value: Nullable<number[]>) =>
      (value ?? []).map((x) => getLabel(x)).join(','),
    (value: Nullable<number[]>) =>
      Array.from(new Set(value ?? []))
        .map((x) => x.toString())
        .join(','),
  );

  const getUserLabel = memoize(
    (value: Nullable<number>) => {
      if (value == null) {
        return null;
      }
      const selected = { firstName: 'John', lastName: 'Doe' }; //users?.find?.((x) => x?.userId === value);

      if (selected == null) {
        return null;
      }

      return fullName(selected);
    },
    (value) => value?.toString() ?? 'null',
  );

  const getMultiUserLabel = memoize(
    (value: Nullable<number[]>) =>
      (value ?? [])
        .map((x) => getUserLabel(x))
        .map((x) => (x ?? '').toLowerCase())
        .sort()
        .join(','),
    (value: Nullable<number[]>) =>
      Array.from(new Set(value ?? []))
        .map((x) => x.toString())
        .join(','),
  );

  const getTeamLabel = memoize(
    (value: Nullable<number>) => {
      if (value == null) {
        return null;
      }
      const selected = { teamName: 'Team 1' }; //teams.find((x) => x.teamId === value);

      if (selected == null) {
        return null;
      }

      return selected?.teamName;
    },
    (value) => value?.toString() ?? 'null',
  );

  const getMultiTeamLabel = memoize(
    (value: Nullable<number[]>) =>
      (value ?? [])
        .map((x) => getTeamLabel(x))
        .map((x) => (x ?? '').toLowerCase())
        .sort()
        .join(','),
    (value: Nullable<number[]>) =>
      Array.from(new Set(value ?? []))
        .map((x) => x.toString())
        .join(','),
  );

  switch (type) {
    case FieldType.SingleSelect:
      return (a: T, b: T) => {
        if (fieldTypeDisplay === FieldTypeDisplay.Level) {
          const a_pos = getPosition(a as any) ?? 0;
          const b_pos = getPosition(b as any) ?? 0;

          return byNumber(a_pos, b_pos);
        }
        const a_value = (getLabel(a as any) ?? '').toLowerCase();
        const b_value = (getLabel(b as any) ?? '').toLowerCase();

        return a_value === b_value ? 0 : a_value > b_value ? 1 : -1;
      };
    case FieldType.MultiSelect:
      return (a: T, b: T) => {
        const a_value = getMultiSelectLabel(a as any) ?? '';
        const b_value = getMultiSelectLabel(b as any) ?? '';

        return a_value === b_value ? 0 : a_value > b_value ? 1 : -1;
      };
    case FieldType.SingleUser:
      return (a: T, b: T) => {
        const a_value = (getUserLabel(a as any) ?? '').toLowerCase();
        const b_value = (getUserLabel(b as any) ?? '').toLowerCase();

        return a_value === b_value ? 0 : a_value > b_value ? 1 : -1;
      };
    case FieldType.MultiUser:
      return (a: T, b: T) => {
        const a_value = getMultiUserLabel(a as any) ?? '';
        const b_value = getMultiUserLabel(b as any) ?? '';

        return a_value === b_value ? 0 : a_value > b_value ? 1 : -1;
      };
    case FieldType.Assignee:
      return (a: T, b: T) => {
        const a_assignee_values: AssigneeValue[] = a ?? ([] as any);
        const b_assignee_values: AssigneeValue[] = b ?? ([] as any);

        const a_users = getMultiUserLabel(
          a_assignee_values.filter(([t]) => t === 'user').map(([, x]) => x),
        );
        const b_users = getMultiUserLabel(
          b_assignee_values.filter(([t]) => t === 'user').map(([, x]) => x),
        );

        if (a_users !== b_users) {
          return a_users > b_users ? 1 : -1;
        }
        const a_teams = getMultiTeamLabel(
          a_assignee_values.filter(([t]) => t === 'team').map(([, x]) => x),
        );
        const b_teams = getMultiTeamLabel(
          b_assignee_values.filter(([t]) => t === 'team').map(([, x]) => x),
        );

        return a_teams === b_teams ? 0 : a_teams > b_teams ? 1 : -1;
      };
    default:
      return undefined;
  }
};
